import React, { useEffect, useState, useCallback, useRef } from 'react';
import BlueprintCard from '../components/BlueprintCard'; // Keep if used for other purposes
import { collection, getDocs, getDoc, doc, query, orderBy, limit, updateDoc, addDoc, serverTimestamp, where, onSnapshot, limit as qLimit } from 'firebase/firestore';
import { database } from '../firebase';
import { useParams, useNavigate } from 'react-router-dom';
import '../components/BlueprintsPage.css';
import { getStorage, ref, getBlob, getDownloadURL, uploadBytes, uploadBytesResumable } from "firebase/storage";
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

import BlueprintScrollViewer from '../components/BlueprintScrollViewer';

// Session-scoped cache so getDownloadURL is only called once per path per browser session
const _resolvedUrlCache = new Map();

export default function BlueprintsPage() {
    const { user, projects } = useAuth();
    const { projectId } = useParams();
    const navigate = useNavigate();

    // --- ALL STATE DECLARATIONS ARE HERE AT THE VERY TOP, IN LOGICAL GROUPS ---
    const storage = getStorage();

    // PDF Upload States
    const [pdfFile, setPdfFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [selectedUploadProjectId, setSelectedUploadProjectId] = useState('');

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file?.type === 'application/pdf') {
            setPdfFile(file);
        } else {
            alert('Please upload a valid PDF file');
        }
    };

    const handleUpload = async () => {
        if (!pdfFile || !user || !selectedUploadProjectId) {
            alert('Please select a project and a PDF first');
            return;
        }
        setUploading(true);
        try {
            const storageRef = ref(storage, `project_blueprints/${selectedUploadProjectId}/${pdfFile.name}`);
            console.log('[UPLOAD] Starting upload → storagePath:', storageRef.fullPath);
            await uploadBytes(storageRef, pdfFile);
            console.log('[UPLOAD] Storage upload complete');
            const blueprintRef = collection(database, 'projects', selectedUploadProjectId, 'blueprints');
            const docRef = await addDoc(blueprintRef, {
                name: pdfFile.name,
                uploadedAt: serverTimestamp(),
                uploadedBy: user.email ?? user.uid,
                storagePath: storageRef.fullPath,
                size: pdfFile.size,
            });
            console.log('[UPLOAD] Firestore doc written → id:', docRef.id, 'storagePath:', storageRef.fullPath);
            alert('PDF uploaded successfully!');
            setPdfFile(null);
            setSelectedUploadProjectId('');
            await fetchBlueprintsList();
        } catch (err) {
            console.error('[UPLOAD] Failed:', err);
            alert('Upload failed');
        }
        setUploading(false);
    };

    // Core Blueprint Data States
    const [blueprintMap, setBlueprintMap] = useState({});
    const [allAnalysisResults, setAllAnalysisResults] = useState({});
    const [currentAnalysisId, setCurrentAnalysisId] = useState(null);
    const [showBlueprintViewer, setShowBlueprintViewer] = useState(false);

    // Loading & Status States
    const [analysisStatus, setAnalysisStatus] = useState({
        isLoading: false,
        message: null,
        error: false,
        currentProjectId: null
    });

    // Photo Viewer States
    const [isPhotoViewerOpen, setIsPhotoViewerOpen] = useState(false);
    const [photosForViewer, setPhotosForViewer] = useState([]);
    const [viewerRoomName, setViewerRoomName] = useState('');
    const [viewerRoomId, setViewerRoomId] = useState(null);
    const [viewerFloorId, setViewerFloorId] = useState(null);
    const [viewerFloorName, setViewerFloorName] = useState('');
    const [viewerBlueprintId, setViewerBlueprintId] = useState(null);
    const [selectedType, setSelectedType] = useState('interior');

    // Photo Upload States
    const [photoFileToUpload, setPhotoFileToUpload] = useState(null);
    const [isPhotoUploading, setIsPhotoUploading] = useState(false);
    const [photoUploadProgress, setPhotoUploadProgress] = useState(0);
    const [photoUploadStatus, setPhotoUploadStatus] = useState({
        isLoading: false,
        message: null,
        error: false,
    });

    const [projectCompanyId, setProjectCompanyId] = useState(null);
    const [companyAttachModal, setCompanyAttachModal] = useState({
        open: false,
        companyId: null,
        companyName: null,
        inFlight: false,
        error: null,
        targetProjectId: null
    });

    const pendingActionRef = useRef(null);

    const getProjectCompanyId = async (targetProjectId) => {
        const p = Array.isArray(projects) ? projects.find(x => x.id === targetProjectId) : null;
        if (p && Object.prototype.hasOwnProperty.call(p, 'companyId')) {
            return p.companyId || null;
        } 
        const snap = await getDoc(doc(database, 'projects', targetProjectId));
        return snap.exists() ? (snap.data().companyId || null) : null;
    };


    const API_BASE = process.env.REACT_APP_PHOTO_API_BASE || 'http://localhost:8080';
    if (!process.env.REACT_APP_PHOTO_API_BASE) {
        console.warn('REACT_APP_PHOTO_API_BASE is not set — photo analysis will only work locally');
    }

    const gsToStoragePath = (gcsPath) => {
        if (!gcsPath) return null;
        const m = String(gcsPath).match(/^gs:\/\/[^/]+\/(.+)$/);
        return m ? m[1] : gcsPath;
    };

    const extractStoragePath = (data) => 
        data?.path || data?.storagePath || gsToStoragePath(data?.gcsPath) || null;

    async function toRenderablePhoto(storage, docSnap) {
        const data = docSnap.data();
        const storagePath = extractStoragePath(data);
        if (!storagePath) return { id: docSnap.id, ...data, src: null, storagePath: null };

        const r = ref(storage, storagePath);

        // Try streaming URL first (fast for images)
        try {
            const url = await withTimeout(getDownloadURL(r), 4000, 'getDownloadURL');
            return { id: docSnap.id, ...data, src: url, storagePath, _loadedVia: 'url' };
        } catch (e) {
            console.warn('getDownloadURL slow/failed → fallback to blob', storagePath, e?.message || e);
        }

        // Fallback to blob (still works if URL path hiccups)
        try {
            const blob = await withTimeout(getBlob(r), 7000, 'getBlob');
            return { id: docSnap.id, ...data, src: URL.createObjectURL(blob), storagePath, _loadedVia: 'blob' };
        } catch (e2) {
            console.warn('Both URL/blob failed for', storagePath, e2?.message || e2);
            return { id: docSnap.id, ...data, src: null, storagePath, _loadedVia: 'none' };
        }
    }


    const withTimeout = (p, ms, label='op') =>
        Promise.race([
            p,
            new Promise((_, rej) => setTimeout(() => rej(new Error(`${label} timeout after ${ms}ms`)), ms))
    ]);



    // --- END ALL STATE DECLARATIONS ---

    useEffect(() => {   
        if (!projectId) { setProjectCompanyId(null); return; }
        const p = Array.isArray(projects) ? projects.find(p => p.id === projectId) : null;
        if (p && Object.prototype.hasOwnProperty.call(p, 'companyId')) {
            setProjectCompanyId(p.companyId || null);
            return;
        }
        (async () => {
            try {
                const snap = await getDoc(doc(database, 'projects', projectId));
                setProjectCompanyId(snap.exists() ? (snap.data().companyId || null) : null);
            } catch (e) {
                console.error('Failed to read project.companyId', e);
                setProjectCompanyId(null);
            }
        })();
    }, [projectId, projects, database]);

    //Fetch user company ID
    const fetchCurrentUserCompanyId = useCallback(async () => {
        if (!user?.uid) return { companyId: null, companyName: null };
        try {
            const userSnap = await getDoc(doc(database, 'users', user.uid));
            const companyId = userSnap.exists() ? (userSnap.data().companyId || null) : null;
            if (!companyId) return { companyId, companyName: null };
            const companySnap = await getDoc(doc(database, 'companies', companyId));
            const companyName = companySnap.exists() ? (companySnap.data().name || "Your company") : 'Your company';
            return { companyId, companyName };
        } catch (error) {
            console.error("Error fetching user company ID:", error);
            return { companyId: null, companyName: null };
        }
    }, [user?.uid, database]);

    const ensureProjectAttachedOrAsk = useCallback(async (targetProjectId) => {
        const attached = await getProjectCompanyId(targetProjectId);
        if (attached) {
            const memberSnap = await getDoc(doc(database, 'companies', attached, 'members', user.uid));
            if (memberSnap.exists()) return { ok: true, companyId: attached };
        }

        
        const { companyId, companyName } = await fetchCurrentUserCompanyId();
        if (!companyId) { 
            setAnalysisStatus({
                isLoading: false,
                message: "This project isn't attached to a company, and your account doesn't have a company yet.",
                error: true,
                currentProjectId: null
        });
        return { ok: false, reason: 'no_company' };
        }
        setCompanyAttachModal({ open: true, companyId, companyName, inFlight: false, error: null, targetProjectId });
        return { ok: false, reason: 'awaiting-fetch' };
    }, [projects, database, fetchCurrentUserCompanyId]);

    // funct to pre check
    const preflightAnalyzePermission = useCallback(async (targetProjectId) => {
        if (!user?.uid) {
            setAnalysisStatus({ isLoading: false, message: "You must be logged in to analyze blueprints.", error: true, currentProjectId: null });
            return { ok: false};
        }
    
        const attachCheck = await ensureProjectAttachedOrAsk(targetProjectId);
        if (!attachCheck.ok) return attachCheck;

        const companyId = attachCheck.companyId;
        try {
            const [companySnap, memberSnap] = await Promise.all([
                getDoc(doc(database, 'companies', companyId)),
                getDoc(doc(database, 'companies', companyId, 'members', user.uid))
                ]);

                if (!companySnap.exists()) {
                    setAnalysisStatus({ isLoading: false, message: "Company not found. Please contact support.", error: true, currentProjectId: null });
                    return { ok: false, reason: 'company_not_found' };
                }
                
                const c = companySnap.data();  
                if (!['active', 'trialing'].includes(c?.stripe?.status)) {
                    setAnalysisStatus({ isLoading: false, message: "Your company's subscription is inactive. Please contact support.", error: true, currentProjectId: null });
                    return { ok: false, reason: 'inactive_plan' };
                }

                if (c?.features?.analysis !== true) {
                    setAnalysisStatus({ isLoading: false, message: "Your plan does not include blueprint analysis. Please upgrade your plan.", error: true, currentProjectId: null });
                    return { ok: false, reason: 'feature_not_in_plan' };
                }

                //Role check
                const role = memberSnap.exists() ? (memberSnap.data().role || 'viewer') : 'viewer';
                if (!['owner', 'manager'].includes(role)) {
                    setAnalysisStatus({ isLoading: false, message: "You don't have permission to analyze. Ask an owner/manager.", error: true, currentProjectId: null });
                    return { ok: false, reason: 'insufficient_role' };
                }

                return { ok: true, companyId, role, company: c};
        } catch (error) {
            console.error("Error checking analysis permissions:", error);
            setAnalysisStatus({ isLoading: false, message: "Failed to check analysis permissions. Please try again.", error: true, currentProjectId: null });
            return { ok: false, reason: 'internal_error' };
        }
    }, [user?.uid, ensureProjectAttachedOrAsk, database]);

    const confirmAttachProjectToCompany = async () => {

        const targetId = companyAttachModal.targetProjectId || projectId;
        if (!companyAttachModal.companyId || !targetId) return;
        setCompanyAttachModal(m => ({ ...m, inFlight: true, error: null }));
        try { 
            await updateDoc(doc(database, 'projects', targetId), {
            companyId: companyAttachModal.companyId,
            companyAttachedAt: serverTimestamp(),
            companyAttachedBy: user?.uid || null
        });
        if (targetId === projectId) setProjectCompanyId(companyAttachModal.companyId);
        setCompanyAttachModal(m => ({ ...m, inFlight: false, open: false, error: null }));

        const action = pendingActionRef.current;
        pendingActionRef.current = null;
        if (action?.type === 'timeline') {
            setTimeout(() => handleAnalyzeRoomTimeline(action.roomId, action.floorId), 0);
        } else if (action?.type === 'blueprint') {
            setTimeout(() => handleAnalyzeBlueprint(action.projectId), 0);
        }

        setAnalysisStatus({ 
            isLoading: false, 
            message: `Project successfully attached to ${companyAttachModal.companyName}. You can now analyze blueprints.`, 
            error: false, 
            currentProjectId: null, 
        });
        } catch (error) {
            setCompanyAttachModal(m => ({ ...m, inFlight: false, error: 'Failed to attach project. Please try again.' }));
        }
    };

    const cancelAttachProjectToCompany = () => {
        pendingActionRef.current = null;
        setCompanyAttachModal({ 
            open: false, companyId: null, companyName: null, 
            inFlight: false, error: null, targetProjectId: null 
        });
    };


    // Function to fetch blueprints list for the project and their latest analysis IDs
    const fetchBlueprintsList = useCallback(async () => {
        let isMounted = true;

        if (!projects || projects.length === 0) {
            console.log("No projects available to fetch blueprint data for list.");
            if (isMounted) {
                setBlueprintMap({});
                // setBlueprintsList([]); // If you need this list, uncomment
            }
            return;
        }

        const map = {};
        // const fetchedBlueprintsForList = []; // If you need this list, uncomment

        const projectsToFetch = projectId ? projects.filter(p => p.id === projectId) : projects;

        for (const project of projectsToFetch) {
            const blueprintsRef = collection(database, "projects", project.id, "blueprints");
            let blueprintDataForProject = { id: null, name: null, storagePath: null, latestAnalysisId: null, createdAt: null, preview_jpg_url: null, firstAnalyzedImageUrl: null };

            try {
                const docsSnap = await getDocs(blueprintsRef);
                const allBlueprints = [];

                docsSnap.forEach((docSnap) => {
                    const data = docSnap.data();
                    if (data.storagePath) {
                        console.log('[FETCH] Found blueprint with storagePath:', data.storagePath, '| id:', docSnap.id);
                        allBlueprints.push({ id: docSnap.id, ...data });
                    } else {
                        console.warn('[FETCH] Skipping blueprint doc (no storagePath):', docSnap.id, data);
                    }
                });

                allBlueprints.sort((a, b) => {
                    const aTime = a.uploadedAt?.toMillis?.() ?? Infinity;
                    const bTime = b.uploadedAt?.toMillis?.() ?? Infinity;
                    return bTime - aTime;
                });

                const latestBlueprintDoc = allBlueprints[0];
                if (latestBlueprintDoc) {
                    blueprintDataForProject = {
                        id: latestBlueprintDoc.id,
                        name: latestBlueprintDoc.name,
                        storagePath: latestBlueprintDoc.storagePath,
                        createdAt: latestBlueprintDoc.uploadedAt?.toDate(),
                        preview_jpg_url: latestBlueprintDoc.preview_jpg_url || null, // Capture direct preview_jpg_url if present
                    };

                    const analysisHistoryRef = collection(database, 'projects', project.id, 'blueprints', latestBlueprintDoc.id, 'analysis_history');
                    const q = query(analysisHistoryRef, orderBy('analyzedAt', 'desc'), limit(1));
                    const querySnapshot = await getDocs(q); // Use getDocs for initial fetch

                    if (!querySnapshot.empty) {
                        const latestAnalysisDoc = querySnapshot.docs[0];
                        blueprintDataForProject.latestAnalysisId = latestAnalysisDoc.id;
                        console.log(`Fetched latest analysis ID ${blueprintDataForProject.latestAnalysisId} for blueprint ${latestBlueprintDoc.id}`);
                        
                        const analysisData = latestAnalysisDoc.data();
                        if (analysisData.allPageImageUrls && analysisData.allPageImageUrls.length > 0) {
                            const first = analysisData.allPageImageUrls[0];
                            const firstPath = first?.storagePath;
                            if (firstPath) {
                                try {
                                    blueprintDataForProject.firstAnalyzedImageUrl =
                                        _resolvedUrlCache.has(firstPath)
                                            ? _resolvedUrlCache.get(firstPath)
                                            : await getDownloadURL(ref(storage, firstPath));
                                    _resolvedUrlCache.set(firstPath, blueprintDataForProject.firstAnalyzedImageUrl);
                                } catch (e) {
                                    console.warn('Could not resolve preview image:', firstPath, e?.message);
                                }
                            }
                        }

                    } else {
                        console.log(`No analysis history found for blueprint ${latestBlueprintDoc.id}.`);
                    }
                }
            } catch (err) {
                console.warn(`⚠️ Error fetching blueprint docs or analysis history for project ${project.id}:`, err.message);
            }

            map[project.id] = blueprintDataForProject;
            // fetchedBlueprintsForList.push({ projectId: project.id, ...blueprintDataForProject }); // If you need this list, uncomment
        }

        if (isMounted) {
            setBlueprintMap(map);
        }
        return () => { isMounted = false; };
    }, [projects, projectId, setBlueprintMap]); // Removed setBlueprintsList as dependency if not used

    // Function to load and listen to analysis data (wrapped in useCallback)
    // THIS IS THE CRITICAL REAL-TIME LISTENER
    const loadAndListenToAnalysis = useCallback(() => {
        let unsubscribeAnalysis = () => {};

        if (!projectId || !blueprintMap[projectId]?.id || !blueprintMap[projectId]?.latestAnalysisId) {
            setShowBlueprintViewer(false); // Ensure viewer is hidden
            setAnalysisStatus({
                isLoading: false,
                message: "Loading blueprint details...",
                error: false,
                currentProjectId: projectId
            });
            // If we have a blueprint ID but no analysis yet, prompt to analyze
            if (projectId && blueprintMap[projectId]?.id && !blueprintMap[projectId]?.latestAnalysisId) {
                setAnalysisStatus(prev => ({ ...prev, message: "No saved analysis found. Click 'Analyze Blueprint' to generate." }));
            }
            return unsubscribeAnalysis; // Return empty unsubscribe
        }

        const blueprintDocId = blueprintMap[projectId].id;
        const analysisIdToLoad = blueprintMap[projectId].latestAnalysisId;

        setAnalysisStatus({
            isLoading: true,
            message: `Loading blueprint analysis for ${blueprintMap[projectId]?.name || projectId}...`,
            error: false,
            currentProjectId: projectId
        });
        setCurrentAnalysisId(analysisIdToLoad);
        setIsPhotoViewerOpen(false); // Close photo viewer if main viewer is loading/changing
        setPhotosForViewer([]);

        const analysisDocRef = doc(database, 'projects', projectId, 'blueprints', blueprintDocId, 'analysis_history', analysisIdToLoad);

        const resolveStoragePath = async (storagePath) => {
            if (_resolvedUrlCache.has(storagePath)) return _resolvedUrlCache.get(storagePath);
            const url = await getDownloadURL(ref(storage, storagePath));
            _resolvedUrlCache.set(storagePath, url);
            return url;
        };

        // Set up real-time listener
        unsubscribeAnalysis = onSnapshot(analysisDocRef, async (docSnap) => {
            if (docSnap.exists()) {
                const raw = docSnap.data();

                // Old analysis docs store `url` instead of `storagePath` — detect and skip them
                const pages = raw.allPageImageUrls || [];
                const hasStoragePaths = pages.length > 0 && pages.some(p => p.storagePath);
                if (pages.length > 0 && !hasStoragePaths) {
                    setAnalysisStatus({ isLoading: false, message: "This analysis was created before our latest update. Please click 'Analyze Blueprint' to re-analyze.", error: false, currentProjectId: null });
                    setShowBlueprintViewer(false);
                    return;
                }

                // Resolve all page storagePaths → download URLs in parallel.
                // allSettled so one failed page doesn't blank the entire viewer.
                const settled = await Promise.allSettled(
                    pages.map(async (page) => {
                        if (!page.storagePath) return { url: null, width: page.width, height: page.height };
                        const url = await resolveStoragePath(page.storagePath);
                        return { url, width: page.width, height: page.height };
                    })
                );
                const resolvedAllPageImageUrls = settled.map((r, i) =>
                    r.status === 'fulfilled'
                        ? r.value
                        : { url: null, width: pages[i]?.width, height: pages[i]?.height }
                );

                // Build path→url map so roomData can reuse the same resolved URLs
                const pathToUrl = {};
                pages.forEach((page, i) => {
                    if (page.storagePath) pathToUrl[page.storagePath] = resolvedAllPageImageUrls[i]?.url ?? null;
                });

                const resolvedRoomData = (raw.roomData || []).map(rd => ({
                    ...rd,
                    url: pathToUrl[rd.storagePath] ?? null,
                }));

                const loadedAnalysisResults = {
                    ...raw,
                    allPageImageUrls: resolvedAllPageImageUrls,
                    roomData: resolvedRoomData,
                };

                const areImagesValid = resolvedAllPageImageUrls.length > 0 &&
                                       resolvedAllPageImageUrls.every(img => img.url);

                const areRoomsValid = resolvedRoomData.length > 0 &&
                                      resolvedRoomData.some(page => page.floors && Array.isArray(page.floors) && page.floors.length > 0);

                setAllAnalysisResults(prev => ({
                    ...prev,
                    [projectId]: loadedAnalysisResults
                }));

                const currentStatus = loadedAnalysisResults.status ?? 'unknown';
                let message = `Analysis status: ${currentStatus.replace(/_/g, ' ')}.`;
                let isError = currentStatus === 'failed';

                if ((currentStatus === 'completed' || currentStatus === 'unknown') && areImagesValid) {
                    setShowBlueprintViewer(true);
                    message = "Analysis complete. Viewer ready.";
                } else if (currentStatus === 'failed') {
                    console.error("Analysis failed. Not showing viewer.");
                    setShowBlueprintViewer(false);
                    message = loadedAnalysisResults.errorMessage || "Blueprint analysis failed. Please try again.";
                } else if (currentStatus === 'processing_images') {
                    message = "Analysis in progress: Processing images...";
                    setShowBlueprintViewer(false);
                } else if (currentStatus === 'processing_rooms') {
                    message = "Analysis in progress: Detecting rooms...";
                    setShowBlueprintViewer(false);
                } else {
                    console.log(`Analysis data incomplete or status '${currentStatus}' not 'completed'. Not showing viewer.`);
                    setShowBlueprintViewer(false);
                }

                setAnalysisStatus({
                    isLoading: currentStatus !== 'completed' && currentStatus !== 'failed',
                    message: message,
                    error: isError,
                    currentProjectId: null // Clear this once status is stable or error
                });

            } else {
                console.log("No analysis data found for this blueprint and analysis ID.");
                setAnalysisStatus({ isLoading: false, message: "No analysis data found.", error: true, currentProjectId: null });
                setShowBlueprintViewer(false);
                setAllAnalysisResults(prev => ({ ...prev, [projectId]: { allPageImageUrls: [], roomData: [] } }));
            }
        }, (err) => {
            console.error("Error listening to analysis document:", err);
            setAnalysisStatus({ isLoading: false, message: "Failed to load real-time analysis data.", error: true, currentProjectId: null });
            setShowBlueprintViewer(false);
            setAllAnalysisResults(prev => ({ ...prev, [projectId]: { allPageImageUrls: [], roomData: [] } }));
        });

        return unsubscribeAnalysis;
    }, [projectId, blueprintMap, database, setAllAnalysisResults, setCurrentAnalysisId, setAnalysisStatus, setShowBlueprintViewer, setIsPhotoViewerOpen, setPhotosForViewer]);

    // Effect to manage the Firestore listener lifecycle for analysis data
    useEffect(() => {
        let unsubscribe = () => {};
        if (projectId) { // Only set up listener if we are on a project-specific page
            unsubscribe = loadAndListenToAnalysis();
        }
        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [projectId, loadAndListenToAnalysis]); // Depend on projectId and the memoized callback

    // Effect to fetch blueprint list on initial load and when projects change
    useEffect(() => {
        if (projects !== null && projects !== undefined) {
            fetchBlueprintsList();
        }
    }, [projects, fetchBlueprintsList]);

    const handleAnalyzeBlueprint = async (currentProjectId) => {

        const gate = await preflightAnalyzePermission(currentProjectId);
        if (!gate.ok) {
            if (gate.reason === 'awaiting-fetch') {
                pendingActionRef.current = { type: 'blueprint', projectId: currentProjectId };
            }
            return;
        }


        if (analysisStatus.isLoading && analysisStatus.currentProjectId === currentProjectId) {
            console.log("Analysis already in progress for this project.");
            return;
        }

        // Always fetch fresh from Firestore so a newly-uploaded PDF is picked up
        let pdfUrl, blueprintId;
        try {
            const bpSnap = await getDocs(collection(database, 'projects', currentProjectId, 'blueprints'));
            const all = [];
            bpSnap.forEach(d => { if (d.data().storagePath) all.push({ id: d.id, ...d.data() }); });
            all.sort((a, b) => (b.uploadedAt?.toMillis?.() ?? Infinity) - (a.uploadedAt?.toMillis?.() ?? Infinity));
            const latest = all[0];
            if (!latest) {
                setAnalysisStatus({ isLoading: false, message: "No blueprint PDF found for this project. Please upload one.", error: true, currentProjectId: null });
                return;
            }
            pdfUrl = await getDownloadURL(ref(storage, latest.storagePath));
            console.log('[ANALYZE] Resolved storagePath →', latest.storagePath, '→ temp URL length:', pdfUrl?.length);
            blueprintId = latest.id;
        } catch (e) {
            setAnalysisStatus({ isLoading: false, message: "Failed to fetch blueprint. Please try again.", error: true, currentProjectId: null });
            return;
        }

        // Clear cached page URLs for this blueprint so re-analysis gets fresh download tokens.
        // Each analysis run uploads to the same GCS paths with a new token, invalidating old URLs.
        for (const key of _resolvedUrlCache.keys()) {
            if (key.startsWith(`project_blueprints/${currentProjectId}/${blueprintId}/pages/`)) {
                _resolvedUrlCache.delete(key);
            }
        }

        setAnalysisStatus({
            isLoading: true,
            message: `Initiating analysis for ${blueprintMap[currentProjectId]?.name || currentProjectId}... This may take a few minutes.`,
            error: false,
            currentProjectId: currentProjectId
        });
        // IMPORTANT: Do NOT clear allAnalysisResults here. Let onSnapshot handle updates.
        setShowBlueprintViewer(false); // Hide viewer during analysis initiation

        try {
            const analyzeResponse = await callWithAuthBlueprint(
                '/analyze-pdf', // Full path relative to API_BASE (or just '/analyze-pdf' if API_BASE is the full function URL)
                {
                    pdfUrl: pdfUrl,
                    projectId: currentProjectId,
                    blueprintId: blueprintId
                }
            );

            console.log("Response from analyze-pdf Cloud Function (Node.js):", analyzeResponse.data);

            if (analyzeResponse.data.analysisId) {
                setCurrentAnalysisId(analyzeResponse.data.analysisId);
                if (projectId && projectId === currentProjectId) {
                    await fetchBlueprintsList();
                } else {
                    navigate(`/projects/${currentProjectId}/blueprints`);
                }
            } else {
                console.error("Backend did not return an analysis ID.");
                setAnalysisStatus({ isLoading: false, message: "Analysis initiated, but no analysis ID returned from backend.", error: true, currentProjectId: null });
            }

        } catch (error) {
            console.error("❌ Error during overall blueprint analysis process:", error.message, error.response?.data);
            let errorMessage = "Analysis failed due to an unknown error.";
            if (axios.isCancel(error) || error.code === 'ECONNABORTED') {
                errorMessage = "Analysis timed out. The PDF might be very large or the network is slow. Please try again.";
            } else if (error.response) {
                errorMessage = `Analysis failed: ${error.response.status} - ${error.response.data?.error || error.response.data?.message || 'Server error'}`;
            } else {
                errorMessage = `Analysis failed: ${error.message}. Check console for details.`;
            }
            setAnalysisStatus({
                isLoading: false,
                message: errorMessage,
                error: true,
                currentProjectId: null
            });
            // Mark the specific project's analysis as failed without clearing data
            setAllAnalysisResults(prev => ({
                ...prev,
                [currentProjectId]: { ...prev[currentProjectId], status: 'failed' }
            }));
            setShowBlueprintViewer(false); // Hide viewer on error
        }
    };

    const handlePreviewClick = async (project) => {
        const storagePath = blueprintMap[project.id]?.storagePath;
        const blueprintDocId = blueprintMap[project.id]?.id;

        if (!storagePath || !blueprintDocId) {
            setAnalysisStatus({
                isLoading: false,
                message: "No PDF found for this project to preview.",
                error: true,
                currentProjectId: null
            });
            return;
        }

        setAnalysisStatus({
            isLoading: true,
            message: `Loading blueprint preview for ${blueprintMap[project.id]?.name || project.id}...`,
            error: false,
            currentProjectId: project.id
        });
        
        // This navigation will trigger the useEffect to load analysis data via loadAndListenToAnalysis
        navigate(`/projects/${project.id}/blueprints`); 
        console.log(`Navigating to /projects/${project.id}/blueprints to open viewer.`);
        // The viewer will be shown by the useEffect once analysis results are loaded via onSnapshot
    };

    const handleSaveAnalysisChanges = async (projectId, blueprintId, analysisId, updatedRoomData) => {
        if (!projectId || !blueprintId || !analysisId || !updatedRoomData) {
            console.error("Missing data for saving analysis changes.");
            setAnalysisStatus({ isLoading: false, message: "Failed to save changes: Missing data.", error: true, currentProjectId: null });
            return;
        }

        setAnalysisStatus({
            isLoading: true,
            message: "Saving blueprint changes...",
            error: false,
            currentProjectId: projectId
        });

        try {
            const analysisDocRef = doc(database, 'projects', projectId, 'blueprints', blueprintId, 'analysis_history', analysisId);
            await updateDoc(analysisDocRef, {
                roomData: updatedRoomData,
                lastEditedAt: serverTimestamp() // Use serverTimestamp for consistency
            });

            // Optimistically update frontend state
            setAllAnalysisResults(prev => ({
                ...prev,
                [projectId]: {
                    ...prev[projectId],
                    roomData: updatedRoomData
                }
            }));

            setAnalysisStatus({
                isLoading: false,
                message: "Changes saved successfully!",
                error: false,
                currentProjectId: null
            });
            console.log("✅ Analysis changes saved to Firestore.");
        } catch (error) {
                console.error("❌ Error saving analysis changes to Firestore:", error.message);
                setAnalysisStatus({
                    isLoading: false,
                    message: `Failed to save changes: ${error.message}`,
                    error: true,
                    currentProjectId: null
                });
            }
        };

    const closeViewer = () => {
        navigate('/blueprints'); // Navigate back to the main blueprints list
        setShowBlueprintViewer(false); // Hide the viewer
        setAnalysisStatus({ isLoading: false, message: null, error: false, currentProjectId: null });
        setCurrentAnalysisId(null);
        // setAllAnalysisResults({}); // Clear all analysis results or just for the current project?
        // Let's clear for the specific project being closed to avoid old data lingering
        setAllAnalysisResults(prev => {
            const newState = { ...prev };
            if (projectId && newState[projectId]) {
                delete newState[projectId];
            }
            return newState;
        });
    };

    const openPhotoViewer = async (room, floorId, floorName) => {
        // set current context for the modal
        setViewerRoomName(room.name);
        setViewerRoomId(room.id);
        setViewerFloorId(floorId);
        setViewerFloorName(floorName);
        setViewerBlueprintId(blueprintMap[projectId]?.id);

        setIsPhotoViewerOpen(true);
        setPhotoUploadStatus({ isLoading: true, message: "Loading photos...", error: false });

        try {
            const currentBlueprintId = blueprintMap[projectId]?.id;

            // ----- guards -----
            if (!projectId) {
            console.error("❌ Error: projectId is undefined.");
            setPhotoUploadStatus({ isLoading: false, message: "Cannot open photo viewer: Project ID is missing from URL.", error: true });
            return;
            }
            if (!currentBlueprintId) {
            console.error("❌ Error: blueprintId is undefined.");
            setPhotoUploadStatus({ isLoading: false, message: "Cannot open photo viewer: Blueprint ID is missing.", error: true });
            return;
            }
            if (!room?.id) {
            console.error("❌ Error: roomId is undefined.");
            setPhotoUploadStatus({ isLoading: false, message: "Cannot open photo viewer: Room ID is missing. Please re-analyze blueprint.", error: true });
            return;
            }
            if (!floorId) {
            console.error("❌ Error: floorId is undefined.");
            setPhotoUploadStatus({ isLoading: false, message: "Cannot open photo viewer: Floor ID is missing for room. Please re-analyze blueprint.", error: true });
            return;
            }

            // ----- query Firestore for this room's photos -----
            const photosRef = collection(database, 'photos');


            const qy = query(
            photosRef,
            where('projectId', '==', projectId),
            where('blueprintId', '==', currentBlueprintId),
            where('roomId', '==', room.id),
            where('floorId', '==', floorId)
            // (optional) .orderBy('date','desc') if you also have a composite index
            );

            const photoDocs = await getDocs(qy);

            // ----- clean up previously created blob: URLs before replacing -----
            photosForViewer.forEach(p => {
            if (p?.src?.startsWith('blob:')) URL.revokeObjectURL(p.src);
            });

            // ----- chunked loading to avoid Storage retry-limit issues -----
            const docs = photoDocs.docs;
            const fetched = [];
            const MAX_AT_ONCE = 3; // sweet spot: 2–4

            setPhotosForViewer([]);

            for (let i = 0; i < docs.length; i += MAX_AT_ONCE) {
                const chunk = docs.slice(i, i + MAX_AT_ONCE);
                
                const settled = await Promise.allSettled(chunk.map(d => toRenderablePhoto(storage, d)));

                const res = settled
                .filter(s => s.status === 'fulfilled')
                .map(s => s.value);

                setPhotosForViewer(prev => {
                    const next = [...prev, ...res];
                    // final sort (if not using progressive render)
                    const toMillis = v => (v?.toMillis?.() ?? (typeof v?.seconds === 'number' ? v.seconds * 1000 : 0));
                    next.sort((a, b) => (toMillis(b.date) || toMillis(b.uploadedAt)) - (toMillis(a.date) || toMillis(a.uploadedAt)));
                    return next;
                });

                if (i === 0) setPhotoUploadStatus(s => ({ ...s, isLoading: false}));
            }

            setPhotoUploadStatus(s => ({ ...s, isLoading: false }));
        } catch (error) {
            console.error("Error fetching photos for room:", error);
            setPhotosForViewer([]);
            setPhotoUploadStatus({
            isLoading: false,
            message: `Failed to load photos: ${error.message}`,
            error: true
            });
        } finally {
            // if you ever early-return above, the finally still runs—keeps UI sane
            setPhotoUploadStatus(s => ({ ...s, isLoading: false }));
        }
    };
    const closePhotoViewer = () => {
        photosForViewer.forEach(p => {
            if (p?.src?.startsWith('blob:')) URL.revokeObjectURL(p.src);
       });

        setIsPhotoViewerOpen(false); // Corrected state name
        setPhotosForViewer([]);
        setViewerRoomName('');
        setViewerRoomId(null);
        setViewerFloorId(null);
        setViewerFloorName('');
        setViewerBlueprintId(null);
        setPhotoFileToUpload(null);
        setIsPhotoUploading(false);
        setPhotoUploadProgress(0);
        setPhotoUploadStatus({ isLoading: false, message: null, error: false });
    };

    const handlePhotoFileChange = (event) => {
        if (event.target.files[0]) {
            setPhotoFileToUpload(event.target.files[0]);
        } else {
            setPhotoFileToUpload(null);
        }
    };

    const handlePhotoUploadToRoom = async () => {
        
        if (!photoFileToUpload || !projectId || !viewerBlueprintId || !viewerRoomId || !viewerFloorId || !user?.uid || !viewerRoomName || !currentAnalysisId) {
            setPhotoUploadStatus({ isLoading: false, message: "Missing photo file or room/project/user information for upload.", error: true });
            return;
        }
            setIsPhotoUploading(true);
            setPhotoUploadProgress(0);
            setPhotoUploadStatus({ isLoading: true, message: "Uploading photo...", error: false });

        try {
            const file = photoFileToUpload;
            const uuid = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
            const storagePath = `project_photos/${projectId}/${viewerBlueprintId}/${viewerFloorId}/${viewerRoomId}/${uuid}.jpg`;
            const storageRef = ref(storage, storagePath);
            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on('state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setPhotoUploadProgress(progress);
                },
                (error) => {
                    console.error("Photo upload failed:", error);
                    setPhotoUploadStatus({ isLoading: false, message: `Photo upload failed: ${error.message}`, error: true });
                    setIsPhotoUploading(false);
                    setPhotoUploadProgress(0);
                },
                async () => {
                    const bucketName = uploadTask.snapshot.ref.bucket;
                    const gcsPath = bucketName ? `gs://${bucketName}/${storagePath}` : null;

                    const photosCollectionRef = collection(database, 'photos');
                    const newPhotoDocRef = await addDoc(photosCollectionRef, {
                        path: storagePath,
                        gcsPath,
                        projectId: projectId,
                        blueprintId: viewerBlueprintId,
                        analysisId: currentAnalysisId,
                        roomId: viewerRoomId,
                        roomName: viewerRoomName,
                        floorId: viewerFloorId,
                        floorName: viewerFloorName,
                        uploadedAt: serverTimestamp(),
                        uploadedBy: user.uid,
                        type: selectedType
                    })

                    let src = null;
                    try {
                    const blob = await getBlob(storageRef);
                    src = URL.createObjectURL(blob);
                    } catch (e) {
                    console.warn('getBlob after upload failed: ', e);
                    }
                    
                    setPhotosForViewer(prev => [...prev, { id: newPhotoDocRef.id, path: storagePath, gcsPath, src }]);
                    setPhotoFileToUpload(null);
                    setIsPhotoUploading(false);
                    setPhotoUploadProgress(0);
                    setPhotoUploadStatus({ isLoading: false, message: "Photo uploaded successfully!", error: false });
                    }
                );

            } catch (error) {
                console.error("Error initiating photo upload:", error);
                setPhotoUploadStatus({ isLoading: false, message: `Error initiating photo upload: ${error.message}`, error: true });
                setIsPhotoUploading(false);
                setPhotoUploadProgress(0);
            }
        };

        async function callWithAuth(path, body) {
            const token = await user.getIdToken();
            const base = (API_BASE || '').replace(/\/+$/,'');
            const p = (`${path || ''}`).startsWith('/') ? path :  `/${path || ''}`;
            return axios.post(`${base}${p}`, body, {
                headers: { Authorization: `Bearer ${token}`},
                timeout: 600000
            });
        }

        async function callWithAuthBlueprint(path, body) {
        const token = await user.getIdToken();
        const fullBase = process.env.REACT_APP_BLUEPRINT_API_BASE || 'http://localhost:8080/generate_blueprint_preview'; // Use your specific ENV var
        const base = fullBase.replace(/\/+$/,'');
        const p = (`${path || ''}`).startsWith('/') ? path :  `/${path || ''}`;
         console.log('Blueprint API call →', `${base}${p}`);

        return axios.post(`${base}${p}`, body, {
            headers: { Authorization: `Bearer ${token}`},
            timeout: 600000 
        });
}

        async function handleAnalyzeRoomTimeline(roomId, floorId) {
        if (!projectId || !viewerBlueprintId || !roomId || !floorId) {
            console.error("Missing parameters for room timeline analysis.");
            setAnalysisStatus({ isLoading: false, message: "Can't analyze: missing project/room/floor context.", error: true, currentProjectId: null });
            return;
        }
        const gate = await preflightAnalyzePermission(projectId);
        if (!gate.ok) {
            if (gate.reason === 'awaiting-fetch') {
                pendingActionRef.current = { type: 'timeline', projectId, roomId, floorId };
            } 
            return;
        }
        setAnalysisStatus({
            isLoading: true,
            message: "Analyzing room photos...",
            error: false,
            currentProjectId: projectId
        });

        try {
            await callWithAuth('/photos/analyze-batch', {
                projectId,
                blueprintId: viewerBlueprintId,
                floorId,
                roomId,
            });

            const projectName = (projects?.find(p => p.id === projectId)?.name) || '';
            const address = (projects?.find(p => p.id === projectId)?.address) || (blueprintMap[projectId]?.address) || '';


            const { data } = await callWithAuth('/rooms/analyze-timeline', {
                projectId,
                blueprintId: viewerBlueprintId,
                floorId,
                roomId,
                roomName: viewerRoomName,
                floorName: viewerFloorName,
                address,
                projectName,
            });

            setAnalysisStatus({
                isLoading: false,
                message: data?.report_url ? "Timeline Ready." : "Timeline generated",
                link: data?.report_url || null,
                error: false,
                currentProjectId: null,
                
            });
            if(data?.report_url){
                try { window.open(data.report_url, '_blank', 'noopener'); } catch {}        
            } 

        } catch (error) {
            console.error("Error analyzing room timeline: ", error?.response?.data || error.message);
            setAnalysisStatus({
                isLoading: false,
                message: `Failed: ${error?.response?.data?.error || error.message}`,
                error: true,
                currentProjectId: null
            });

        }
    };


        return (
            <div className="blueprints-container">
                <h2>Blueprints</h2>

                {analysisStatus.isLoading && analysisStatus.currentProjectId && (
                    <div className="status-message loading">
                        <div className="spinner"></div>
                        <span>{analysisStatus.message}</span>
                    </div>
                )}
                {analysisStatus.message && !analysisStatus.isLoading && (
                    <div className={`status-message ${analysisStatus.error ? 'error' : 'info'}`}>
                        <span>
                            {analysisStatus.message}
                            {analysisStatus.link && (
                                <> &mdash; <a href={analysisStatus.link} target="_blank" rel="noopener noreferrer">Open report</a></>
                            )}
                        </span>
                        <button
                            onClick={() => setAnalysisStatus({ isLoading: false, message: null, error: false, currentProjectId: null, link: null })}
                            className="dismiss-btn"
                        >
                            Dismiss
                        </button>
                    </div>
                )}

                {projectId ? (
                    allAnalysisResults[projectId]?.allPageImageUrls?.length > 0 && showBlueprintViewer ? (
                        <BlueprintScrollViewer
                            allPageImageUrls={allAnalysisResults[projectId].allPageImageUrls.map(img =>
                                typeof img === 'string' ? { url: img, width: null, height: null } : img
                            )}
                            roomData={allAnalysisResults[projectId].roomData}
                            projectId={projectId}
                            blueprintId={blueprintMap[projectId]?.id}
                            analysisId={currentAnalysisId}
                            onSaveAnalysisChanges={handleSaveAnalysisChanges}
                            onClose={closeViewer}
                            onRoomDotClick={openPhotoViewer}
                        />
                    ) : (
                        <div className="loading-or-empty">
                            {analysisStatus.isLoading && analysisStatus.currentProjectId === projectId ? (
                                <>
                                    <div className="spinner"></div>
                                    <p>{analysisStatus.message}</p>
                                </>
                            ) : (
                                <>
                                    <p>{blueprintMap[projectId]?.storagePath ? `Ready to analyze: ${blueprintMap[projectId]?.name || projectId}` : `No blueprint PDF found for this project. Please upload one.`}</p>
                                    {blueprintMap[projectId]?.storagePath && (
                                        <button
                                            onClick={() => handleAnalyzeBlueprint(projectId)}
                                            disabled={analysisStatus.isLoading && analysisStatus.currentProjectId === projectId}
                                            className="analyze-blueprint-btn"
                                        >
                                            {analysisStatus.isLoading && analysisStatus.currentProjectId === projectId ? 'Analyzing...' : 'Analyze Blueprint'}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    )
                ) : (
                    <div className="blueprint-grid">
                        {projects == null ? (
                            <div className="loading-or-empty">
                                <div className="spinner"></div>
                                <p>Loading projects...</p>
                            </div>
                        ) : projects.length === 0 ? (
                            <div className="loading-or-empty">
                                <p>No projects found. Create a project and upload your first blueprint to get started.</p>
                            </div>
                        ) : (
                            projects.map((project) => {
                                const projectBlueprint = blueprintMap[project.id];
                                const previewImageUrl = projectBlueprint?.preview_jpg_url || projectBlueprint?.firstAnalyzedImageUrl || "/blueprint-placeholder.jpg";
                                const isAnalyzed = !!projectBlueprint?.latestAnalysisId;
                                const isAnalyzing = analysisStatus.isLoading && analysisStatus.currentProjectId === project.id;

                                return (
                                    <div className="blueprint-card" key={project.id}>
                                        <div
                                            className="blueprint-card__image-wrap"
                                            onClick={() => isAnalyzed && handlePreviewClick(project)}
                                            style={{ cursor: isAnalyzed ? 'pointer' : 'default' }}
                                        >
                                            <img
                                                src={previewImageUrl}
                                                alt="Blueprint Preview"
                                                className="blueprint-image"
                                                onError={(e) => {
                                                    if (e.target.src !== window.location.origin + "/blueprint-placeholder.jpg") {
                                                        e.target.onerror = null;
                                                        e.target.src = "/blueprint-placeholder.jpg";
                                                    }
                                                }}
                                            />
                                            <span className={`blueprint-card__badge ${isAnalyzed ? 'blueprint-card__badge--analyzed' : 'blueprint-card__badge--none'}`}>
                                                {isAnalyzed ? 'Analyzed' : 'No Analysis'}
                                            </span>
                                        </div>
                                        <div className="blueprint-card__body">
                                            <h3 className="blueprint-title">{project.address || project.name || 'Unnamed Project'}</h3>
                                            <div className="blueprint-card__actions">
                                                {isAnalyzed && (
                                                    <button className="bp-btn bp-btn--ghost" onClick={() => handlePreviewClick(project)}>
                                                        View Blueprint
                                                    </button>
                                                )}
                                                {projectBlueprint?.storagePath && (
                                                    <button
                                                        className="bp-btn bp-btn--primary"
                                                        onClick={() => handleAnalyzeBlueprint(project.id)}
                                                        disabled={isAnalyzing}
                                                    >
                                                        {isAnalyzing ? 'Analyzing...' : isAnalyzed ? 'Re-Analyze' : 'Analyze'}
                                                    </button>
                                                )}
                                            </div>
                                            <div className="blueprint-card__upload">
                                                <span className="blueprint-card__upload-label">Upload PDF</span>
                                                <input
                                                    type="file"
                                                    accept="application/pdf"
                                                    onChange={(e) => {
                                                        setSelectedUploadProjectId(project.id);
                                                        handleFileChange(e);
                                                    }}
                                                />
                                                {pdfFile && selectedUploadProjectId === project.id && (
                                                    <button
                                                        className="bp-btn bp-btn--ghost"
                                                        onClick={handleUpload}
                                                        disabled={uploading}
                                                    >
                                                        {uploading ? 'Uploading...' : 'Upload PDF'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}

                {/* Photo Viewer Modal */}
                {isPhotoViewerOpen && (
                    <div className="photo-viewer-modal-overlay">
                        <div className="photo-viewer-modal-content">
                            <button onClick={closePhotoViewer} className="close-btn">✕</button>
                            <h3>Photos &mdash; {viewerRoomName}{viewerFloorName && ` · ${viewerFloorName}`}</h3>

                            {photoUploadStatus.message && (
                                <div className={`status-message ${photoUploadStatus.error ? 'error' : 'info'}`}>
                                    <span>{photoUploadStatus.message}</span>
                                </div>
                            )}
                            {photoUploadStatus.isLoading && <div className="spinner" style={{ margin: '10px auto' }}></div>}

                            {photosForViewer.length > 0 ? (
                                <div className="photo-grid">
                                    {photosForViewer.map(photo => (
                                        <img
                                            key={photo.id}
                                            src={photo.src || "/blueprint-placeholder.jpg"}
                                            alt="Room Photo"
                                            className="room-photo-thumbnail"
                                            loading="lazy"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = "https://placehold.co/150x150/0e2b55/9adfff?text=Error";
                                            }}
                                        />
                                    ))}
                                </div>
                            ) : (
                                !photoUploadStatus.isLoading && <p style={{ color: 'var(--bp-text-muted)', margin: '16px 0' }}>No photos yet. Upload the first one below.</p>
                            )}

                            <div className="blueprint-card__upload" style={{ marginTop: '20px' }}>
                                <span className="blueprint-card__upload-label">Upload New Photo</span>
                                <input
                                    type="file"
                                    accept="image/jpeg, image/png, image/gif, image/webp"
                                    onChange={handlePhotoFileChange}
                                    disabled={isPhotoUploading}
                                />
                                {photoFileToUpload && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginTop: '6px' }}>
                                        <select
                                            value={selectedType}
                                            onChange={(e) => setSelectedType(e.target.value)}
                                            disabled={isPhotoUploading}
                                            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--bp-border)', background: 'var(--bp-navy-800)', color: 'var(--bp-text)', fontSize: 13 }}
                                        >
                                            <option value="interior">Interior</option>
                                            <option value="exterior">Exterior</option>
                                        </select>
                                        <button
                                            className="bp-btn bp-btn--primary"
                                            onClick={handlePhotoUploadToRoom}
                                            disabled={isPhotoUploading}
                                        >
                                            {isPhotoUploading ? `Uploading ${photoUploadProgress.toFixed(0)}%` : 'Upload Photo'}
                                        </button>
                                    </div>
                                )}
                                {isPhotoUploading && (
                                    <progress value={photoUploadProgress} max="100" style={{ width: '100%', marginTop: '8px', accentColor: 'var(--bp-sky-400)' }}></progress>
                                )}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                                <button
                                    className="bp-btn bp-btn--primary"
                                    onClick={() => handleAnalyzeRoomTimeline(viewerRoomId, viewerFloorId)}
                                    title="Analyze all photos in this room to generate a timeline report"
                                >
                                    Analyze Timeline
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Company Attach Modal */}
                {companyAttachModal.open && (
                    <div className="photo-viewer-modal-overlay">
                        <div className="photo-viewer-modal-content">
                            <button onClick={cancelAttachProjectToCompany} className="close-btn">✕</button>
                            <h3>Attach Project to Company</h3>
                            <p style={{ color: 'var(--bp-text-muted)', fontSize: 14, lineHeight: 1.6, margin: '8px 0 0' }}>
                                This project is not linked to any company. Attach it to{' '}
                                <strong style={{ color: '#fff' }}>{companyAttachModal.companyName || 'your company'}</strong>{' '}
                                to enable company features and billing.
                            </p>
                            {companyAttachModal.error && (
                                <div className="status-message error" style={{ marginTop: '14px' }}>
                                    <span>{companyAttachModal.error}</span>
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                                <button
                                    className="bp-btn bp-btn--primary"
                                    onClick={confirmAttachProjectToCompany}
                                    disabled={companyAttachModal.inFlight}
                                >
                                    {companyAttachModal.inFlight ? 'Attaching...' : 'Confirm Attach'}
                                </button>
                                <button
                                    className="bp-btn bp-btn--danger"
                                    onClick={cancelAttachProjectToCompany}
                                    disabled={companyAttachModal.inFlight}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }